// Every open Taytan tab receives the same `message:notify`, so tabs agree on
// who alerts: the focused tab if there is one, otherwise a single "leader"
// (lowest id). Each tab broadcasts its focus and open conversation.
//
// Tabs heartbeat and silent ones expire: a tab that vanished without a "bye"
// (crash, bfcache, Vite hot reload) must never stay leader and swallow alerts.
// Erring the other way — two tabs both alerting — is harmless for OS
// notifications, which the `tag` merges into one.

type TabState = { focused: boolean; visible: boolean; activeConversationId: number | null };
type PresenceMsg =
    | ({ kind: 'state'; tabId: string } & TabState)
    | { kind: 'hello'; tabId: string }
    | { kind: 'bye'; tabId: string };

const HEARTBEAT_MS = 4000;
const EXPIRE_MS = 10000;

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const others = new Map<string, TabState & { seenAt: number }>();
let own: TabState = { focused: false, visible: false, activeConversationId: null };

const channel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('taytan-message-popup') : null;

const post = (msg: PresenceMsg) => {
    try {
        channel?.postMessage(msg);
    } catch {
        // channel closed — this tab just acts alone
    }
};

const isVisible = () => document.visibilityState === 'visible';
const isFocused = () => isVisible() && document.hasFocus();

const announce = () => post({ kind: 'state', tabId, ...own });

export const updateOwnPresence = (patch: Partial<TabState>) => {
    own = { ...own, ...patch };
    announce();
};

const liveOthers = () => {
    const cutoff = Date.now() - EXPIRE_MS;
    for (const [id, t] of others) if (t.seenAt < cutoff) others.delete(id);
    return others;
};

if (channel) {
    channel.onmessage = ({ data }: MessageEvent<PresenceMsg>) => {
        if (!data || data.tabId === tabId) return;
        if (data.kind === 'bye') others.delete(data.tabId);
        else if (data.kind === 'hello') announce();
        else
            others.set(data.tabId, {
                focused: data.focused,
                visible: data.visible,
                activeConversationId: data.activeConversationId,
                seenAt: Date.now()
            });
    };
}

if (typeof window !== 'undefined') {
    own.focused = isFocused();
    own.visible = isVisible();
    const refresh = () => updateOwnPresence({ focused: isFocused(), visible: isVisible() });
    const bye = () => post({ kind: 'bye', tabId });
    window.addEventListener('focus', refresh);
    window.addEventListener('blur', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('pagehide', bye);
    // Back from bfcache: re-introduce ourselves.
    window.addEventListener('pageshow', (e) => e.persisted && refresh());
    const heartbeat = setInterval(announce, HEARTBEAT_MS);
    post({ kind: 'hello', tabId });
    announce();

    // Dev: a hot-reloaded copy of this module shuts the previous one down, so
    // it can't linger as a ghost tab that heartbeats and wins leadership.
    // (A window hook rather than import.meta.hot, which the shared tsconfig rejects.)
    const w = window as typeof window & { __taytanTabPresenceStop?: () => void };
    w.__taytanTabPresenceStop?.();
    w.__taytanTabPresenceStop = () => {
        clearInterval(heartbeat);
        window.removeEventListener('focus', refresh);
        window.removeEventListener('blur', refresh);
        document.removeEventListener('visibilitychange', refresh);
        window.removeEventListener('pagehide', bye);
        bye();
        channel?.close();
    };
}

export type AlertDecision = 'skip' | 'card' | 'system';

/**
 * What this tab should do with a new message in `conversationId`.
 * `systemAllowed` — OS notifications are permitted in this browser.
 */
export const decideAlert = (conversationId: number, systemAllowed: boolean): AlertDecision => {
    const live = liveOthers();
    const all = [...live.values()];
    let decision: AlertDecision;

    if (isFocused()) {
        decision = own.activeConversationId === conversationId ? 'skip' : 'card';
    } else if (all.some((t) => t.focused)) {
        // Another tab is in front of the user — it handles (or skips) the alert.
        decision = 'skip';
    } else {
        const isLeader = [tabId, ...live.keys()].sort()[0] === tabId;
        if (systemAllowed) {
            // Nobody is looking: one tab raises the OS notification.
            decision = isLeader ? 'system' : 'skip';
        } else if (isVisible()) {
            // No OS notifications: card in the tab that is on screen (browser
            // window behind another app), or failing that in the leader tab.
            decision = 'card';
        } else {
            decision = !all.some((t) => t.visible) && isLeader ? 'card' : 'skip';
        }
    }

    console.log('[message-popup] decision', {
        conversationId,
        decision,
        systemAllowed,
        focused: isFocused(),
        visible: isVisible(),
        activeConversationId: own.activeConversationId,
        otherTabs: all.length
    });
    return decision;
};
